import { InjectionToken } from '@angular/core';
import type {
  AppStore,
  CountryCode,
  LanguageCode,
  SeoSegments,
} from '@handyhand/i18n';
import type { AppEnv } from '../../environments/app-env';

/**
 * Country resolved from the request `Host` at bootstrap. Use this token
 * anywhere you need to branch on market (DK vs NL).
 */
export const CountryToken = new InjectionToken<CountryCode>('CountryToken');

/**
 * Host-driven environment object. Shape matches `AppEnv`. Every service,
 * component, helper, and route factory that previously imported
 * `environment.dk` (or any `environment.*.ts`) must now read this token
 * instead so the values follow the request host.
 */
export const EnvToken = new InjectionToken<AppEnv>('EnvToken');

/**
 * Materialized SEO URL vocabulary for the current request's country, sourced
 * from `@handyhand/i18n`'s `SEO_SEGMENTS` table. Replaces the old
 * `AppEnv['seo']` block: components and services read URL segments here
 * instead of from the environment object.
 */
export const SeoSegmentsToken = new InjectionToken<SeoSegments>(
  'SeoSegmentsToken',
);

/**
 * UI language for the current request. Resolved from the host (with optional
 * `languageOverride` per domain) and falling back to the country's
 * defaultLanguage. Returns an `@handyhand/i18n` `LanguageCode`
 * (`'da' | 'en' | 'nl'`).
 */
export const LanguageToken = new InjectionToken<LanguageCode>('LanguageToken');

/**
 * App-store URLs for the current request's country, sourced from
 * `@handyhand/i18n`. Use `ios` / `android` for the platform-specific store
 * badges and `universal` for a single device-agnostic "download" link.
 */
export const AppStoreToken = new InjectionToken<AppStore>('AppStoreToken');
